export class AccordionInitializer {
	public init = () => {
		// A URL fragment pointing to an entry wins over "first item open"
		const hashTarget = this.getHashTarget();
		if (hashTarget !== null) {
			this.setOpen(hashTarget, true, false);
		}

		document.querySelectorAll<HTMLElement>('.accordion-item').forEach((accordionItem) => {
			const trigger = accordionItem.querySelector('.accordion-item-header');
			if (trigger === null) return;

			if (this.isFirstChildOfSelector(accordionItem, '.features-accordion')) {
				accordionItem.classList.add('open');

				const details = accordionItem.querySelector<HTMLElement>('.accordion-content');
				if (details === null) return;

				setTimeout(() => {
					// Calculate the height of the submenu
					const accordionItemHeight = details.scrollHeight + 'px';
					details.style.height = accordionItemHeight;
				}, 100);
			}

			// Check on initial script load because items can be open by default.
			if (accordionItem.classList.contains('open')) {
				setTimeout(() => {
					this.toggleAccordionItem(accordionItem, false);
				}, 0);
			}

			trigger.addEventListener('click', () => {
				this.setOpen(accordionItem, !accordionItem.classList.contains('open'));
				this.updateHash(accordionItem);
			});
		});

		window.addEventListener('hashchange', this.openFromHash);
	};

	/**
	 * Opens or closes an item. Inside [data-accordion-single-open], opening an item closes the others.
	 */
	public setOpen = (accordionItem: HTMLElement, open: boolean, animate = true) => {
		if (open) {
			accordionItem
				.closest('[data-accordion-single-open]')
				?.querySelectorAll<HTMLElement>('.accordion-item.open')
				.forEach((otherItem) => {
					if (otherItem !== accordionItem) {
						this.setOpen(otherItem, false, animate);
					}
				});
		}

		accordionItem.classList.toggle('open', open);

		const trigger = accordionItem.querySelector('.accordion-item-header');
		if (trigger?.hasAttribute('aria-expanded')) {
			trigger.setAttribute('aria-expanded', String(open));
		}

		this.toggleAccordionItem(accordionItem, animate);
	};

	/**
	 * Opens the entry the URL fragment points to, e.g. after a click on a link to "#faq-lieferzeiten".
	 */
	public openFromHash = () => {
		const target = this.getHashTarget();
		if (target === null) return;

		// Without animation: entries closing above would move the target out of view again
		this.setOpen(target, true, false);
		target.scrollIntoView({ block: 'start' });
	};

	private getHashTarget = (): HTMLElement | null => {
		let id: string;
		try {
			id = decodeURIComponent(window.location.hash.slice(1));
		} catch {
			return null;
		}
		if (id === '') return null;

		const target = document.getElementById(id);
		return target !== null && target.matches('.accordion-item[data-accordion-anchor]') ? target : null;
	};

	/**
	 * Keeps the URL fragment on the entry opened last, so a shared link shows that entry.
	 */
	private updateHash = (accordionItem: HTMLElement) => {
		if (!accordionItem.hasAttribute('data-accordion-anchor') || accordionItem.id === '') return;

		const hash = '#' + accordionItem.id;
		// replaceState: no history entry per click, no jump and no hashchange event
		if (accordionItem.classList.contains('open')) {
			if (window.location.hash !== hash) {
				history.replaceState(history.state, '', hash);
			}
		} else if (window.location.hash === hash) {
			history.replaceState(history.state, '', window.location.pathname + window.location.search);
		}
	};

	private toggleAccordionItem = (accordionItem: HTMLElement, animate = true) => {
		const details = accordionItem.querySelector<HTMLElement>('.accordion-content');
		if (details === null) return;

		if (accordionItem.classList.contains('open')) {
			if (!animate) {
				details.style.height = 'auto';
				return;
			}
			// Calculate the height of the submenu
			details.style.height = details.scrollHeight + 'px';
			// Then "auto", so content that grows later (lazy loaded images, narrower window) is not cut off
			this.afterHeightTransition(details, () => {
				if (accordionItem.classList.contains('open')) {
					details.style.height = 'auto';
				}
			});
		} else {
			if (animate && details.style.height === 'auto') {
				// The transition needs a start value in px
				details.style.height = details.scrollHeight + 'px';
				void details.offsetHeight;
			}
			// Reset the height to 0
			details.style.height = '0';
		}
	};

	private afterHeightTransition = (element: HTMLElement, callback: () => void) => {
		const durations = getComputedStyle(element)
			.transitionDuration.split(',')
			.map((duration) => parseFloat(duration))
			.filter((duration) => Number.isFinite(duration));
		// Without a transition (e.g. prefers-reduced-motion) there is no transitionend event
		if (Math.max(0, ...durations) === 0) {
			callback();
			return;
		}

		const onTransitionEnd = (event: TransitionEvent) => {
			if (event.target !== element || event.propertyName !== 'height') return;
			element.removeEventListener('transitionend', onTransitionEnd);
			callback();
		};
		element.addEventListener('transitionend', onTransitionEnd);
	};

	isFirstChildOfSelector(el: Element, parentSelector: string): boolean {
		const parent = el.parentElement;
		if (!parent) return false;

		// find the first child inside the parent that matches the selector
		const firstMatching = parent.querySelector(`${parentSelector} > *`);

		return firstMatching === el;
	}
}
